import { aiService } from "@/services/aiService";

const SOURCE_MAX = 3000;
const TITLE_HARD_MAX = 60;

/** Normalize streamed model output into a single-line title. */
export function normalizeStreamedTitle(raw: string): string {
  return raw
    .trim()
    .replace(/^["']|["']$/g, "")
    .replace(/\n/g, " ")
    .trim();
}

/** Truncate plain text for use as a non-AI title fallback. */
export function fallbackTitleFromPlainText(plainText: string, maxLen = 50): string {
  const oneLine = plainText.replace(/\s+/g, " ").trim();
  if (!oneLine) return "";
  const cleaned = oneLine.replace(/^\[Deep Research\]\s*/i, "").trim();
  if (!cleaned) return "";
  return cleaned.length > maxLen ? `${cleaned.slice(0, Math.max(0, maxLen - 3)).trim()}...` : cleaned;
}

function buildConversationPrompt(sourceText: string): string {
  const excerpt = sourceText.trim().slice(0, SOURCE_MAX);
  return `Based on the following conversation, generate a title in this exact format:
KEYWORD: descriptive phrase

Rules:
- KEYWORD should be 1-2 words in ALL CAPS identifying the broad subject (what the user is looking for when scanning threads)
- After the colon, add a punchy descriptive phrase (not a full sentence)
- Total length: maximum 50 characters
- Be specific, not generic
- Examples: "PYTHON: debugging async errors", "STARTUP: pitch deck structure", "HEALTH: sleep optimization tips"

Conversation:
${excerpt}

Respond with ONLY the title in the format "KEYWORD: phrase", nothing else. No quotes, no explanation.`;
}

function buildDocumentPrompt(sourceText: string): string {
  const excerpt = sourceText.trim().slice(0, SOURCE_MAX);
  return `Based on the following document excerpt, generate a title in this exact format:
KEYWORD: descriptive phrase

Rules:
- KEYWORD should be 1-2 words in ALL CAPS identifying the broad subject
- After the colon, add a punchy descriptive phrase (not a full sentence)
- Total length: maximum 50 characters
- Be specific to this document, not generic

Document excerpt:
${excerpt}

Respond with ONLY the title in the format "KEYWORD: phrase", nothing else. No quotes, no explanation.`;
}

function buildDocumentDescriptorPrompt(sourceText: string): string {
  const excerpt = sourceText.trim().slice(0, SOURCE_MAX);
  return `Based on the following document excerpt, generate only a short descriptive title fragment.

Rules:
- Do NOT include a broad subject prefix
- Do NOT include a colon
- Do NOT include quotes, bullets, numbering, or dates
- Return a short descriptive phrase, not a full sentence
- Maximum length: 40 characters
- Be specific to this document excerpt

Document excerpt:
${excerpt}

Respond with ONLY the descriptive phrase, nothing else.`;
}

async function streamTitleFromPrompt(userPrompt: string): Promise<string> {
  let generated = "";
  const titleMessages = [
    {
      role: "system" as const,
      content:
        "You are a helpful assistant that creates concise, accurate titles. Respond only with the title.",
    },
    { role: "user" as const, content: userPrompt },
  ];

  for await (const chunk of aiService.streamChat({
    model: "openai/gpt-4o-mini",
    messages: titleMessages,
    stream: true,
    temperature: 0.3,
    maxTokens: 60,
    app: "auto_title",
  })) {
    if (chunk.type === "delta") {
      generated += chunk.content || "";
    } else if (chunk.type === "error") {
      console.error("AI title generation error:", chunk.error);
      generated = "";
      break;
    }
  }

  return normalizeStreamedTitle(generated);
}

function capTitleLength(title: string): string {
  if (title.length <= TITLE_HARD_MAX) return title;
  return `${title.slice(0, TITLE_HARD_MAX - 3).trim()}...`;
}

/**
 * Generate a short title from plain text (conversation transcript or document body).
 * Uses the same model and shape as legacy thread metadata titling.
 */
export async function autoTitleFromPlainText(
  sourceText: string,
  mode: "conversation" | "document" | "document_descriptor",
  options?: { fallbackText?: string }
): Promise<string> {
  const trimmed = sourceText.trim();
  const fallbackSource = (options?.fallbackText ?? trimmed).trim();
  const effectiveFallback = fallbackSource ? fallbackTitleFromPlainText(fallbackSource) : "";

  if (!trimmed && !effectiveFallback) return "";

  try {
    const userPrompt =
      mode === "conversation"
        ? buildConversationPrompt(trimmed || fallbackSource)
        : mode === "document"
          ? buildDocumentPrompt(trimmed || fallbackSource)
          : buildDocumentDescriptorPrompt(trimmed || fallbackSource);

    let title = await streamTitleFromPrompt(userPrompt);

    if (!title && effectiveFallback) {
      title = effectiveFallback;
    }

    if (!title) return "";

    return capTitleLength(title);
  } catch (error) {
    console.error("Error generating title from plain text:", error);
    return effectiveFallback ? capTitleLength(effectiveFallback) : "";
  }
}
